// src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import LoadingSpinner from "./components/LoadingSpinner";
import LoadingPage from "./components/LoadingPage";
import Login from "./components/Auth/Login";
import Logout from "./components/Auth/Logout";
import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import Dashboard from "./pages/Dashboard";
import Drivers from "./pages/Drivers";
import Trucks from "./pages/Trucks";
import Trailers from "./pages/Trailers";
import Trips from "./pages/Trips";
import Finance from "./pages/Finance";
import RoutesPlanning from "./pages/RoutePlanning";
import Complaints from "./pages/Complaints";
import Documents from "./pages/Documents";
import CostTemplates from "./pages/CostTemplates";
import ProtectedRoute from "./components/ProtectRoute";
import Settings from "./pages/Settings";
import Profile from "./pages/Profile";

function AppLayout() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

  useEffect(() => {
    let timer;

    const resetTimer = () => {
      console.log("User activity detected, resetting inactivity timer");
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (auth.currentUser) {
          console.log("Inactivity timeout triggered, logging out");
          auth.signOut().then(() => {
            navigate("/logout");
          }).catch((error) => {
            console.error("Inactivity logout error:", error);
          });
        }
      }, INACTIVITY_TIMEOUT);
    };

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((event) => window.addEventListener(event, resetTimer));

    resetTimer(); // Start timer on mount

    return () => {
      clearTimeout(timer);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
      console.log("Cleaned up inactivity timer");
    };
  }, [navigate]);

  const handleSidebarToggle = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

  return (
    <div className="app-layout">
      <Sidebar onToggle={handleSidebarToggle} isCollapsed={sidebarCollapsed} />
      <div className={`main-content ${sidebarCollapsed ? "expanded" : ""}`}>
        <Topbar isSidebarCollapsed={sidebarCollapsed} />
        <div className="page-body">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/drivers" element={<Drivers />} />
            <Route path="/trucks" element={<Trucks />} />
            <Route path="/trips" element={<Trips />} />
            <Route path="/trailers" element={<Trailers />} />
            <Route path="/routes-planning" element={<RoutesPlanning />} />
            <Route path="/finance" element={<Finance />} />
            <Route path="/complaints" element={<Complaints />} />
            <Route path="/cost-templates" element={<CostTemplates />} /> 
            <Route path="/documents" element={<Documents />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/profile" element={<Profile />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Clear localStorage on app load to prevent auto-login
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("user");

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("App auth state:", user ? "Logged in" : "Not logged in");
      setLoading(false);
    });

    return () => {
      console.log("App auth cleanup");
      unsubscribe();
    };
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/loading" element={<LoadingPage />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}